import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import 'tailwind.css';
import 'index.css';
import App from 'App';
import * as serviceWorker from 'serviceWorker';
import { NotificationProvider } from 'utility/notification';

// Libraries
import { BrowserRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/fr';
import StatusCheck from 'context/StatusCheck';
import { TooltipProvider } from 'components/ui/tooltip';

// Internationalization
import { i18n } from '@lingui/core';
import { Trans } from '@lingui/macro';
import { I18nProvider, useLingui } from '@lingui/react';
import { messages as enMessages } from './locales/en/messages';
import { messages as frMessages } from './locales/fr/messages';
import { en, fr } from 'make-plural/plurals';

// Stripe
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// ***** DayJS Config ***** //

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

// [English] Updates the strings output when calling relativeTime functions like Dayjs.fromNow()
dayjs.updateLocale('en', {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'a few seconds',
        m: '1m',
        mm: '%dm',
        h: '1h',
        hh: '%dh',
        d: '1d',
        dd: '%dd',
        M: '1mo',
        MM: '%dmo',
        y: '1y',
        yy: '%dy',
    },
});

// [French] Updates the strings output when calling relativeTime functions like Dayjs.fromNow()
dayjs.updateLocale('fr', {
    relativeTime: {
        future: 'dans %s',
        past: 'il y a %s',
        s: 'quelques s',
        m: '1 min',
        mm: '%d min',
        h: '1 h',
        hh: '%d h',
        d: '1 j',
        dd: '%d j',
        M: '1 mois',
        MM: '%d mois',
        y: '1 an',
        yy: '%d ans',
    },
});

// ***** Lingui Config ***** //

i18n.loadLocaleData('en', { plurals: en });
i18n.loadLocaleData('fr', { plurals: fr });

i18n.load({
    en: enMessages,
    fr: frMessages,
});

// Initialize language to the browser default
let defaultLang = navigator.language.toLocaleLowerCase();

// Check if user set their own language in localStorage
const lang = localStorage.getItem('store_lang');

if (['en', 'fr'].includes(lang)) {
    defaultLang = lang;
}

if (defaultLang.includes('fr')) {
    i18n.activate('fr');
} else {
    i18n.activate('en');
}

// ***** Stripe Config ***** //

// Initialize Stripe from js.stripe.com/v3/ script tag
const stripePromise = loadStripe(process.env.REACT_APP_PUB_STRIPE_KEY, {
    apiVersion: '2023-10-16',
});

function Root() {
    return (
        <Elements stripe={stripePromise}>
            <I18nProvider i18n={i18n}>
                <DocumentLanguage />
                <NotificationProvider>
                    <StatusCheck>
                        <TooltipProvider delayDuration={300}>
                            <BrowserRouter>
                                <div className="content">
                                    <App />
                                </div>

                                <Footer />
                            </BrowserRouter>
                        </TooltipProvider>
                    </StatusCheck>
                </NotificationProvider>
            </I18nProvider>
        </Elements>
    );
}

function DocumentLanguage() {
    const { i18n } = useLingui();

    useEffect(() => {
        document.documentElement.lang = i18n.locale;
        dayjs.locale(i18n.locale);
    }, [i18n.locale]);

    return null;
}

function Footer() {
    return (
        <footer
            style={{
                backgroundColor: 'rgba(14, 18, 21, 0.8)',
                borderTop: '1px solid rgb(38, 43, 49)',
                color: '#fff',
                flexShrink: 0,
            }}
        >
            <div className="max-w-screen-xl w-full mx-auto flex flex-col md:flex-row items-center p-8 gap-4">
                <span className="text-sm text-[#aaa]">
                    © {new Date().getFullYear()} FrontDoor+
                </span>
                <Link to="/tickets" className="text-sm text-primary no-underline">
                    <Trans>Find My Tickets</Trans>
                </Link>
                <Link to="/" className="text-sm text-[#aaa] no-underline">
                    <Trans>Upcoming Events</Trans>
                </Link>
                <a
                    href="https://frontdoorplus.com/help/"
                    className="text-sm text-[#aaa] no-underline"
                >
                    <Trans>Support</Trans>
                </a>
                <a
                    href="https://frontdoorplus.com/privacy-policy"
                    className="text-sm text-[#aaa] no-underline"
                >
                    <Trans>Privacy Policy</Trans>
                </a>
                <div className="flex gap-4 md:ml-auto">
                    <a
                        href="https://www.facebook.com/FrontDoor.Plus"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#aaa]"
                        aria-label="Facebook"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                    </a>
                    <a
                        href="https://www.instagram.com/frontdoor.plus"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#aaa]"
                        aria-label="Instagram"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                    </a>
                    <a
                        href="https://www.linkedin.com/company/frontdoorplus"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#aaa]"
                        aria-label="LinkedIn"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                    </a>
                </div>
                <p className="text-sm text-[#aaa] no-underline">{process.env.REACT_APP_VERSION}</p>
            </div>
        </footer>
    );
}

const container = createRoot(document.getElementById('root'));

container.render(<Root />);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
